import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { PRIMITIVE_TEXTURE_GLSL } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, snoise, POINT_SPRITE_GLSL, ZONE_GLSL_COMMON, ZONE_GLSL_FRAG } from '../shaderChunks'

/**
 * Volumetric point cloud. The points are spread across a spherical shell
 * with thickness (which gives depth, so it doesn't look like a mesh) and shift
 * according to their frequency band and the bass. Small points + low alpha =>
 * dust/nebula instead of a solid white mass.
 */
export class PointCloud extends BasePrimitive {
  object3D: THREE.Points
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute float aFreqIndex;
        attribute float aSeed;
        uniform float uTime, uBass, uTreble, uLevel, uBeat, uReactivity, uBassPunch, uPixelRatio, uPointScale;
        uniform vec3 uSeed;
        uniform float uSeedPulse;
        uniform sampler2D uFreq;
        varying float vMag;
        varying float vSeed;
        varying vec3 vDir;
        ${snoise}
        ${ZONE_GLSL_COMMON}
        void main() {
          vSeed = aSeed;
          float mag = texture2D(uFreq, vec2(aFreqIndex, 0.5)).r;
          vMag = mag;
          vec3 dir = normalize(position);
          vDir = dir;
          // Smooth organic motion + reactive radial push. The wobble is an
          // always-on idle wave (applied below, outside the zone gate) so the
          // cloud never looks fully static.
          float wobble = snoise(position * 0.9 + uTime * 0.12) * 0.10;
          float disp = (mag * 0.9 + uBass * uBassPunch * 0.8 + uBeat * 0.4) * uReactivity;
          // Seed-driven beat punch: ONLY on the beat (uBeat decays to 0 between
          // beats, so the base shape stays original). The spatial pattern of the
          // punch — which regions pop out — is set by the seed.
          float lobe = snoise(dir * 2.2 + uSeed);
          float seedDisp = lobe * uSeedPulse * uReactivity * 0.9;
          // Zone reaction: local outward push under the moving shapes.
          float zone = zoneDisp(dir) * 0.9;
          // When zone reaction is on, the rest of the cloud is frozen — only the
          // zones react.
          float base = (disp + seedDisp) * (1.0 - uZoneEnabled);
          vec3 pos = position + dir * (base + zone + wobble);
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mv;
          // Points with distance attenuation (reference ~6 units).
          float px = (2.4 + mag * 4.5 + uBeat * 3.0) * (0.7 + aSeed * 0.6);
          gl_PointSize = clamp(px * (6.0 / -mv.z), 1.2, 18.0) * uPixelRatio * uPointScale;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        ${PRIMITIVE_TEXTURE_GLSL}
        uniform float uHue, uTreble, uLevel;
        varying float vMag;
        varying float vSeed;
        varying vec3 vDir;
        ${hsv2rgb}
        ${POINT_SPRITE_GLSL}
        ${ZONE_GLSL_COMMON}
        ${ZONE_GLSL_FRAG}
        void main() {
          // Sprite coverage for the chosen point shape (circle/square/triangle),
          // soft glow or hard opaque.
          float core = pointMask();
          if (core < 0.01) discard;
          float hue = fract(uHue + vMag * 0.22 + uTreble * 0.12 + vSeed * 0.08);
          float bright = 0.35 + vMag * 0.55 + vSeed * 0.15 + uLevel * 0.15;
          vec3 col = hsv2rgb(vec3(hue, 0.8, bright));
          // Soft: low alpha so additive accumulation glows. Solid: fully opaque.
          float alpha = uPointSolid > 0.5 ? core : core * (0.16 + vMag * 0.5);
          // Zone reaction: points inside a shape take its color and glow.
          vec4 z = zoneColor(vDir);
          alpha = max(alpha, z.a * core);
          if (alpha < 0.003) discard;
          vec3 outc = mix(applyPrimitiveTexture(col * core), z.rgb * core, z.a);
          gl_FragColor = vec4(outc, alpha);
        }
      `,
    })
    this.object3D = new THREE.Points(new THREE.BufferGeometry(), this.material)
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    // Solid points occlude (normal blending + depth); soft points glow (additive).
    const solid = params.pointSolid === true
    this.material.blending = solid ? THREE.NormalBlending : THREE.AdditiveBlending
    this.material.depthWrite = solid
  }

  rebuild(params: VisualParams): void {
    // Many small points -> dense dust cloud, not a mesh.
    const count = Math.floor(70000 * (params.density ?? 1))
    const positions = new Float32Array(count * 3)
    const freqIndex = new Float32Array(count)
    const seed = new Float32Array(count)
    const baseR = 2.2

    for (let i = 0; i < count; i++) {
      // Uniformly random direction on the sphere (normalized gaussians):
      // avoids the "banding" of the Fibonacci spiral in volume.
      let x = gaussian()
      let y = gaussian()
      let z = gaussian()
      const len = Math.hypot(x, y, z) || 1
      x /= len
      y /= len
      z /= len
      // Shell with thickness: radius between ~0.55 and 1.0 of the base => volume with depth.
      const r = baseR * (0.55 + 0.45 * Math.cbrt(Math.random()))
      positions[i * 3 + 0] = x * r
      positions[i * 3 + 1] = y * r
      positions[i * 3 + 2] = z * r
      freqIndex[i] = Math.abs(y) // latitude -> band (equator = lows, poles = highs)
      seed[i] = Math.random()
    }

    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aFreqIndex', new THREE.BufferAttribute(freqIndex, 1))
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1))
    geo.computeBoundingSphere()
  }
}

/** Standard gaussian sample (Box-Muller). */
function gaussian(): number {
  let u = 0
  let v = 0
  while (u === 0) u = Math.random()
  while (v === 0) v = Math.random()
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}
